import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from '../../library/button/button';
import Typography from '../../library/typography/typography';
import {
  MessageDotsRegularIcon,
  MessagesDuotoneIcon
} from '../../shared/icons/icons';

import { useIdleTimer } from '../../shared/hooks/hooks';
import useThirdPartyChat from '../../shared/hooks/use-third-party-chat/use-third-party-chat';

import { LIVE_PERSON_CHAT_BUTTON_IDS } from '../../shared/constants/live-person-chat-btn-ids';
import { LIVE_CHAT_POPOUT_MESSAGES } from '../../shared/constants/messages';
import {
  CHAT_SCRIPT_CHECK_INTERVAL,
  CHAT_SCRIPT_MAX_CHECKS
} from '../../shared/constants/units';

import styles from './third-party-chat-button.scss';

const POPOUT_STATE = {
  IDLE: 'IDLE',
  INTERACTED: 'INTERACTED',
  SHOW: 'SHOW'
};

interface ThirdPartyChatButtonProps {
  hidePopout?: boolean;
  popoutText?: string;
}

function ThirdPartyChatButton(props: ThirdPartyChatButtonProps) {
  const { hidePopout = false, popoutText = LIVE_CHAT_POPOUT_MESSAGES.DEFAULT } =
    props;
  const [popoutState, setPopoutState] = useState(POPOUT_STATE.IDLE);
  const idleTimeout = CHAT_SCRIPT_CHECK_INTERVAL * CHAT_SCRIPT_MAX_CHECKS;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>();

  const handleLaunchChat = useCallback(
    () => setPopoutState(POPOUT_STATE.INTERACTED),
    []
  );

  const { isLoading, launchChat } = useThirdPartyChat({
    livePersonChatButtonId: LIVE_PERSON_CHAT_BUTTON_IDS.CEC_CONTACT_US,
    onLaunchChat: handleLaunchChat
  });

  const handleShowPopout = useCallback(
    () =>
      popoutState === POPOUT_STATE.IDLE && setPopoutState(POPOUT_STATE.SHOW),
    [popoutState]
  );

  function handleClose() {
    setPopoutState(POPOUT_STATE.INTERACTED);
  }

  useIdleTimer(handleShowPopout, idleTimeout);

  useEffect(() => {
    if (popoutState === POPOUT_STATE.SHOW && !timerRef.current) {
      timerRef.current = setTimeout(() => {
        setPopoutState(POPOUT_STATE.INTERACTED);
      }, idleTimeout);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [idleTimeout, popoutState]);

  return (
    <>
      {!isLoading && (
        <div className={styles['live-chat-container']}>
          {popoutState === POPOUT_STATE.SHOW && !hidePopout && (
            <div className={styles['live-chat-popout-container']}>
              <Button
                ariaLabel="Close Live Chat"
                className={styles['live-chat-close']}
                onClick={handleClose}
              >
                <div className={styles['live-chat-close-x']}>X</div>
                <div className={styles['live-chat-close-text']}>CLOSE</div>
              </Button>
              <div className={styles['live-chat-popout']}>
                <div className={styles['popout-top-section']}>
                  <MessagesDuotoneIcon className={styles['messages-icon']} />
                  <Typography className={styles['popout-text']}>
                    {popoutText}
                  </Typography>
                </div>
                <Button
                  ariaLabel="Live Chat Popout Chat Now"
                  className={styles['popout-chat-now-btn']}
                  onClick={launchChat}
                >
                  CHAT NOW
                </Button>
              </div>
            </div>
          )}
          <Button
            ariaLabel="Live Chat Button"
            className={styles['live-chat-main-btn']}
            onClick={launchChat}
          >
            <MessageDotsRegularIcon
              className={styles['live-chat-message-icon']}
            />
            Live Chat
          </Button>
        </div>
      )}
    </>
  );
}

export default ThirdPartyChatButton;
